/* eslint-disable react/no-unknown-property */
import {
  Grid,
  ICON_TROPHY_OUTLINE,
  Message,
  Spacing,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { delay } from 'lodash';
import React from 'react';
import { Agent, IndividualGoal } from '../../../providers/api/dtos';
import { createIndividualGoal } from '../../../providers/api/goal/goalProvider';
import { currentYear } from '../../../utils/time/time';
import { AgentGoalSimulationResultView, AgentGoalSimulationResultViewProps } from '../../components/agent-goal-simulation-result-view/AgentGoalSimulationResultView';
import GoalTargetForm, { GoalTargetFormModel } from '../../components/goal-target-form/GoalTargetForm';
import * as styles from './AgentGoalSimulationView.style';
import TutorialView from '../../../components/tutorial-view/TutorialView';
import { AGENT_GOAL_VIDEO_TUTORIAL } from '../../../constants';

export interface AgentGoalSimulationViewProps {
  agentId: NonNullable<Agent['id']>,
  goal?: IndividualGoal,
  hideTutorial?: boolean,
  onGoalAccepted?: AgentGoalSimulationResultViewProps['onGoalAccepted'],
}

export const SIMULATION_DELAY = 2000;
export const SIMULATION_ERROR_MESSAGE = 'Non è stato possibile effettuare la simulazione dell\'obiettivo di guadagno';

export const AgentGoalSimulationView: React.FC<AgentGoalSimulationViewProps> = ({
  agentId,
  goal,
  hideTutorial = false,
  onGoalAccepted,
}) => {
  const [isSimulationLoading, setIsSimulationLoading] = React.useState(false);
  const [hasSimulationError, setHasSimulationError] = React.useState(false);
  const [target, setTarget] = React.useState<number | undefined>(goal?.target);
  const [simulationGoals, setSimulationGoals] = React.useState<IndividualGoal | undefined>(goal);

  const onSimulationSubmit = React.useCallback(async (values: GoalTargetFormModel) => {
    const targetNumber = parseFloat(values.target);
    setTarget(targetNumber);
    setIsSimulationLoading(true);
    setHasSimulationError(false);

    delay(async () => {
      try {
        const result = await createIndividualGoal({
          agentId,
          target: targetNumber,
        }, { simulate: true });
        setIsSimulationLoading(false);
        setSimulationGoals(result);
      } catch (error) {
        setIsSimulationLoading(false);
        setHasSimulationError(true);
      }
    }, SIMULATION_DELAY);
  }, [agentId]);

  const onInternalGoalAccepted = React.useCallback((newAcceptedGoal: IndividualGoal) => {
    onGoalAccepted?.(newAcceptedGoal!);
  }, [onGoalAccepted]);

  return (
    <Spacing padding={[150, 0, 0]}>
      <Grid gutter={75}>
        <Grid.Unit size={{ MD: !hideTutorial ? 1 / 2 : 1 }}>
          <Message
            icon={ICON_TROPHY_OUTLINE}
            message={goal
              ? `Il tuo obiettivo di guadagno annuale per il ${currentYear()} è di ${formatEuro(goal.target)}`
              : `Inserisci il tuo obiettivo di guadagno annuale per il ${currentYear()}`}
          >
            L&apos;importo da inserire rappresenta il guadagno netto che desideri ottenere e non include la quota trattenuta da Dove.it.
          </Message>
          <div css={styles.simulationForm}>
            <GoalTargetForm
              initialValues={{ target: goal?.target?.toString() || '' }}
              minTarget={5_000}
              loading={isSimulationLoading}
              onSubmit={onSimulationSubmit}
            />
          </div>
        </Grid.Unit>

        {!hideTutorial && (
          <Grid.Unit size={{ MD: 1 / 2 }}>
            <TutorialView {...AGENT_GOAL_VIDEO_TUTORIAL} />
          </Grid.Unit>
        )}

        <Grid.Unit>
          {(target && !hasSimulationError) && (
            <Spacing margin={[400, 0, 0]}>
              <AgentGoalSimulationResultView
                agentId={agentId}
                target={target}
                goal={simulationGoals}
                loading={isSimulationLoading}
                onGoalAccepted={onInternalGoalAccepted}
              />
            </Spacing>
          )}
          {hasSimulationError && (
            <Spacing margin={[400, 0, 0]}>
              <Message
                message={SIMULATION_ERROR_MESSAGE}
                type="critical"
                boxed
              />
            </Spacing>
          )}
        </Grid.Unit>
      </Grid>
    </Spacing>
  );
};
